import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SearchBar from "@/components/feature/SearchBar";

const navItems = [
  { label: "首页", path: "/" },
  { label: "影视作品", path: "/works" },
  { label: "明星资讯", path: "/news" },
  { label: "关于本站", path: "/about" },
];

interface NavbarProps {
  overlay: boolean;
}

export default function Navbar({ overlay }: NavbarProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const transparent = overlay && !scrolled && !menuOpen;

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-3 px-4 md:h-[68px] md:gap-6 md:px-6">
        <Link
          to="/"
          className="flex shrink-0 cursor-pointer items-center gap-2.5"
          aria-label="天海翼影视内容站 首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
            翼
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-base font-bold tracking-wide ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              天海翼影视
            </span>
            <span
              className={`mt-0.5 hidden text-[10px] tracking-wider md:block ${
                transparent ? "text-background-50/70" : "text-foreground-500"
              }`}
            >
              影视作品 · 明星资讯
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? "text-accent-500"
                  : transparent
                    ? "text-background-50/90 hover:text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto hidden w-64 lg:block xl:w-72">
          <SearchBar variant={transparent ? "dark" : "light"} />
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className={`ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md md:hidden ${
            transparent ? "text-background-50" : "text-foreground-900"
          }`}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
        </button>
      </div>

      <div className="hidden px-4 pb-3 md:block lg:hidden">
        <div className="mx-auto max-w-[1280px]">
          <SearchBar variant={transparent ? "dark" : "light"} />
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <div className="mb-4">
            <SearchBar />
          </div>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between border-b border-background-100 py-3 text-base font-medium transition-colors ${
                  isActive(item.path) ? "text-accent-600" : "text-foreground-800"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}